import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import Styles from './DualChart.module.scss'
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartPt } from 'src/models'
import dayjs, { Dayjs } from 'dayjs'
import { ceil, debounce, floor, isNil, last, max, maxBy, min, minBy, range, round } from 'lodash'
import { Marker } from './Marker'
import { FormatedDate, timeToDDMMHHmm } from './utils'


export const DualChart: React.FC<{
  className: string
  syncId: string
  data: ChartPt[]
  xLabel?: string
  yLabel?: string
  yAxisUnits: [string, string]
  lineColors?: [string, string]
  noMinor?: boolean
  grid?: boolean
  current?: Dayjs
  height?: number
  width?: number
  zoom?: { x: number; scale: number }
}> = ({
  className,
  syncId,
  data,
  xLabel,
  yLabel,
  yAxisUnits,
  lineColors,
  noMinor,
  grid,
  current,
  height,
  width,
  zoom,
}) => {
    const values = [
      data.map(({ v }) => v),
      data.map(({ v2 }) => v2),
    ]
    const times = data.map(({ t }) => t)
    const minY = [minBy(values[0]) ?? 0, minBy(values[1]) ?? 0]
    const maxY = [maxBy(values[0]) ?? 0, maxBy(values[1]) ?? 0]
    const _minX = min(times) ?? 0
    const _maxX = max(times) ?? 0
    const minX = zoom ? zoom.x - (zoom.x - _minX) * zoom.scale : _minX
    const maxX = zoom ? zoom.x + (_maxX - zoom.x) * zoom.scale : _maxX
    const yAxis = [
      [round(floor(minY[0]) - 1, 0), round(ceil(maxY[0]) + 1, 0)],
      [round(floor(minY[1]) - 1, 0), round(ceil(maxY[1]) + 1, 0)],
    ]
    const [yTickMajorInterval, yTicks] = useMemo(() => {
      const yRange = yAxis.map(v => v[1] - v[0])
      const yOrder = yRange.map(v => Math.floor(Math.log10(v * 0.7)))
      const yTickMinorInterval = yOrder.map(v => Math.pow(10, v - 1))
      const yTickMajorInterval = yOrder.map(v => Math.pow(10, v) * 2)
      const yTicks: number[][] = [[], []]
      if (noMinor) {
        for (let i of [0, 1]) {
          yTicks[i].push(
            ...range(
              floor(yAxis[i][0] / yTickMajorInterval[i]) * yTickMajorInterval[i],
              yAxis[i][1],
              yTickMajorInterval[i],
            ).map(v => round(v, 1)),
          )
          yTicks[i].push(last(yTicks[i])! + yTickMajorInterval[i])
        }
      } else {
        for (let i of [0, 1]) {
          yTicks[i].push(
            ...range(
              floor(yAxis[i][0] / yTickMinorInterval[i]) * yTickMinorInterval[i],
              yAxis[i][1],
              yTickMinorInterval[i],
            ).map(v => round(v, 1)),
          )
        }
      }
      return [yTickMajorInterval, yTicks]
    }, [yAxis, noMinor])
    const xTicksFull = useMemo(() => {
      const indexes = ceil(dayjs(_maxX).diff(dayjs(_minX), 'hour'))
      const interval = Math.ceil(indexes / 12)
      const start = dayjs(_minX).startOf('hour').unix() * 1000
      return range(indexes).map(i => start + i * interval * 3600_000)
    }, [_minX, _maxX])
    const xTicks = xTicksFull.filter(v => v >= minX && v <= maxX)

    return (
      <div className={classNames(Styles.chart, className, zoom && Styles.scrollable)}>
        <div className={Styles.units}>
          {yAxisUnits[0] && <label className={Styles.unit} style={{ color: lineColors?.[0] }}>{yAxisUnits[0]}</label>}
          <div className={Styles.spacer} />
          {yAxisUnits[1] && <label className={Styles.unit} style={{ color: lineColors?.[1] }}>{yAxisUnits[1]}</label>}
        </div>
        <ResponsiveContainer width={width ?? "100%"} height={height ?? 270}>
          <LineChart
            syncId={syncId}
            margin={{
              top: 40,
              bottom: xLabel ? 12 : -6,
              left: yLabel ? 6 : -24,
              right: 28,
            }}
            data={data}
          >
            {grid && <CartesianGrid className={Styles.grid} strokeDasharray="3 5" />}
            <YAxis
              orientation="left"
              yAxisId="v"
              dataKey="v"
              className={Styles.YAxis}
              type="number"
              label={yLabel && <Label className={Styles.yLabel} value={yLabel} />}
              tickMargin={noMinor ? 1 : -7}
              fontSize="1rem"
              domain={yAxis[0]}
              interval={0}
              ticks={yTicks[0]}
              tickFormatter={v => (noMinor ? v : v % yTickMajorInterval[0] === 0 ? `${v}➖` : '')}
            />
            <YAxis
              orientation="right"
              yAxisId="v2"
              dataKey="v2"
              className={Styles.YAxis}
              type="number"
              label={yLabel && <Label className={Styles.yLabel} value={yLabel} />}
              tickMargin={noMinor ? 1 : -7}
              fontSize="1rem"
              domain={yAxis[1]}
              interval={0}
              ticks={yTicks[1]}
              tickFormatter={v => (noMinor ? v : v % yTickMajorInterval[1] === 0 ? `${v}➖` : '')}
            />
            <XAxis
              className={Styles.XAxis}
              type="number"
              allowDataOverflow={true}
              label={<Label className={Styles.xLabel} value={xLabel} />}
              fontSize="1rem"
              dataKey="t"
              tickFormatter={timeToDDMMHHmm}
              ticks={xTicks}
              domain={[minX, maxX]}
            />
            <Line
              yAxisId="v"
              dataKey={`v`}
              stroke={lineColors?.[0]}
              strokeWidth={2}
              dot={() => <></>}
            />
            <Line
              yAxisId="v2"
              dataKey={`v2`}
              stroke={lineColors?.[1]}
              strokeWidth={2}
              dot={() => <></>}
            />
            {/* <Tooltip
              content={({
                active,
                payload,
                label,
              }: {
                active?: boolean
                payload?: any
                label?: number
              }) => {
                const data = payload?.[0]
                return (
                  active &&
                  (!isNil(payload?.[0]) || !isNil(payload?.[1])) &&
                  !isNil(label) && (
                    <div className={Styles.chartTooltips}>
                      <div className={Styles.time}>{FormatedDate(label)}</div>
                      <div className={Styles.value}>{`${payload?.[0].value}${yAxisUnits[0] ?? ''}`}</div>
                      <div className={Styles.value}>{`${payload?.[1].value}${yAxisUnits[1] ?? ''}`}</div>
                    </div>
                  )
                )
              }}
            /> */}
          </LineChart>
        </ResponsiveContainer>
        <div className={Styles.scrollHint}>
          <label>{'scroll to zoom'}</label>
        </div>
      </div>
    )
  } 